import React, { useState } from "react";
import { X, ChevronRight, Phone } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { sendContactEmail } from "../lib/emailjs";

interface OfferteFormProps {
  isOpen: boolean;
  onClose: () => void;
}

function OfferteForm({ isOpen, onClose }: OfferteFormProps) {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    success?: boolean;
    message?: string;
  }>({});

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    category: "",
    phone: "",
    subject: "",
    description: "",
  });

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({});

    try {
      const result = await sendContactEmail({
        ...formData,
        description: `${formData.subject}\n\n${formData.description}`,
      });

      if (result?.success) {
        setFormData({
          name: "",
          email: "",
          phone: "",
          category: "",
          subject: "",
          description: "",
        });
        onClose();
        navigate("/bedankt-offerte");
      } else {
        setSubmitStatus({
          success: false,
          message:
            result.error ||
            "Er is iets misgegaan. Probeer het later opnieuw of neem telefonisch contact op.",
        });
      }
    } catch (error) {
      setSubmitStatus({
        success: false,
        message:
          "Er is iets misgegaan. Probeer het later opnieuw of neem telefonisch contact op.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <div className="fixed bottom-0 right-0 z-[999999] transition-opacity duration-300">
      <div
        className={`bg-slate-50 rounded-t-2xl shadow-xl overflow-y-scroll transform transition-transform duration-300 ease-out md:w-[400px] w-full ${
          isOpen ? "translate-y-0" : "translate-y-full"
        }`}
        style={{ maxHeight: "75vh" }}
      >
        <div className="flex flex-col h-full">
          <div className="flex sticky top-0 z-50 justify-between items-center p-4 bg-[#FFD100] text-white rounded-t-2xl">
            <h2 className="text-lg font-medium">Direct offerte of contact</h2>
            <button
              onClick={onClose}
              className="text-white hover:text-white/80 transition-colors p-1"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-4 md:p-6">
            <div className="relative flex flex-col items-center space-x-2">
              <p className="text-[#17354A] mb-4 text-md font-bold">
                Liever telefonisch contact?
              </p>
              <a
                href="tel:+31852129778"
                className="mb-4 relative font-medium transition-all duration-150 leading-normal outline-none inline-flex border-2 border-[#17354A] items-center justify-center rounded-full active:scale-[.98] disabled:cursor-not-allowed text-base px-5 h-12 leading-[48px] text-[#17354A] hover:bg-[#E6B800] hover:text-white focus:opacity-60 w-full md:w-fit"
                style={{ minWidth: "216px" }}
              >
                <span className="flex items-center gap-1">
                  <Phone className="w-4 h-4" />
                  Bel direct 085-2129778
                </span>
              </a>

              <p className="text-[#17354A] mb-4 text-md font-bold text-center">
                Of vul de formulier hieronder in en wij zullen contact met u
                opnemen.
              </p>
            </div>

            <form className="space-y-4" onSubmit={handleSubmit}>
              <div>
                <div className="relative">
                  <input
                    className="block w-full bg-white text-black appearance-none text-base outline-none border-solid rounded-xl border border-[#E5E7EB] transition-all shadow-none duration-150 hover:border-[#17354A]/50 focus:border-[#17354A] box-border h-14 pt-4 px-4 placeholder:opacity-0"
                    id="name"
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                  />
                  <label className="absolute top-2 origin-left pointer-events-none left-4 pr-4 transition-all duration-150 text-gray-500 text-sm">
                    Naam
                  </label>
                </div>
              </div>

              <div className="relative">
                <input
                  className="block w-full bg-white text-black appearance-none text-base outline-none border-solid rounded-lg border border-[#E5E7EB] transition-all shadow-none duration-150 hover:border-[#17354A]/50 focus:border-[#17354A] box-border h-14 pt-4 px-4 placeholder:opacity-0"
                  id="phone"
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                />
                <label className="absolute top-2 origin-left pointer-events-none left-4 pr-4 transition-all duration-150 text-gray-500 text-sm">
                  Telefoonnummer
                </label>
              </div>

              <div>
                <div className="relative">
                  <input
                    className="block w-full bg-white text-black appearance-none text-base outline-none border-solid rounded-xl border border-[#E5E7EB] transition-all shadow-none duration-150 hover:border-[#17354A]/50 focus:border-[#17354A] box-border h-14 pt-4 px-4 placeholder:opacity-0"
                    id="email"
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                  />
                  <label className="absolute top-2 origin-left pointer-events-none left-4 pr-4 transition-all duration-150 text-gray-500 text-sm">
                    E-mailadres
                  </label>
                </div>
              </div>

              <div>
                <div className="relative">
                  <select
                    className="block w-full bg-white text-black appearance-none text-base outline-none border-solid rounded-xl border border-[#E5E7EB] transition-all shadow-none duration-150 hover:border-[#17354A]/50 focus:border-[#17354A] box-border h-14 pt-4 px-4"
                    id="category"
                    name="category"
                    required
                    value={formData.category}
                    onChange={handleInputChange}
                  >
                    <option value="">-</option>
                    <option value="renovatie">Renovatie</option>
                    <option value="groepenkast">Groepenkast</option>
                    <option value="elektra">Elektra aanleggen</option>
                    <option value="laadpaal">Laadpaal installatie</option>
                    <option value="overig">Overig</option>
                  </select>
                  <label className="absolute top-2 origin-left pointer-events-none left-4 pr-4 transition-all duration-150 text-gray-500 text-sm">
                    Categorie
                  </label>
                </div>
              </div>

              <div>
                <div className="relative">
                  <input
                    className="block w-full bg-white text-black appearance-none text-base outline-none border-solid rounded-xl border border-[#E5E7EB] transition-all shadow-none duration-150 hover:border-[#17354A]/50 focus:border-[#17354A] box-border h-14 pt-4 px-4 placeholder:opacity-0"
                    id="subject"
                    type="text"
                    name="subject"
                    required
                    value={formData.subject}
                    onChange={handleInputChange}
                  />
                  <label className="absolute top-2 origin-left pointer-events-none left-4 pr-4 transition-all duration-150 text-gray-500 text-sm">
                    Onderwerp
                  </label>
                </div>
              </div>

              <div>
                <div className="relative">
                  <textarea
                    className="block w-full bg-white text-black appearance-none text-base outline-none border-solid rounded-xl border border-[#E5E7EB] transition-all shadow-none duration-150 hover:border-[#17354A]/50 focus:border-[#17354A] box-border h-32 pt-4 px-4 resize-none"
                    id="description"
                    name="description"
                    required
                    value={formData.description}
                    onChange={handleInputChange}
                  />
                  <label className="absolute top-2 origin-left pointer-events-none left-4 pr-4 transition-all duration-150 text-gray-500 text-sm">
                    Beschrijving
                  </label>
                </div>
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  className={`relative font-medium transition-all duration-150 leading-normal outline-none inline-flex border-2 border-transparent items-center justify-center rounded-full active:scale-[.98] disabled:cursor-not-allowed text-base px-5 h-12 leading-[48px] bg-[#F47B20] text-white hover:bg-[#F47B20]/80 focus:bg-[#F47B20]/80 focus-visible:border-crustaLight disabled:bg-[#F47B20]/40 w-full md:w-fit ${
                    isSubmitting ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Verzenden..." : "Verzenden"}
                  <ChevronRight className="w-5 h-5" />
                </button>
                {submitStatus.message && (
                  <div
                    className={`mt-4 p-4 rounded-lg text-center ${
                      submitStatus.success
                        ? "bg-green-50 text-green-800"
                        : "bg-red-50 text-red-800"
                    }`}
                  >
                    {submitStatus.message}
                  </div>
                )}
              </div>
              <p className="text-center text-[#17354A] mb-4 text-sm">
                We nemen binnen één werkdag contact met u op, per e-mail of
                telefonisch.
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OfferteForm;
